import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

interface SessionDb extends DBSchema {
  meta: {
    key: string
    value: string
  }
}

let dbPromise: Promise<IDBPDatabase<SessionDb>> | null = null

function getDb(): Promise<IDBPDatabase<SessionDb>> {
  if (!dbPromise) {
    dbPromise = openDB<SessionDb>('zion-session', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta')
      },
    })
  }
  return dbPromise
}

const SESSION_MIRROR_KEY = 'zion_session_token_mirror'

export async function getSessionToken(): Promise<string | null> {
  const db = await getDb()
  const fromIdb = (await db.get('meta', 'session_token'))?.trim()
  if (fromIdb) return fromIdb
  try {
    return localStorage.getItem(SESSION_MIRROR_KEY)?.trim() || null
  } catch {
    return null
  }
}

export async function saveSessionToken(token: string): Promise<void> {
  const trimmed = token.trim()
  const db = await getDb()
  await db.put('meta', trimmed, 'session_token')
  try {
    localStorage.setItem(SESSION_MIRROR_KEY, trimmed)
  } catch {
    /* quota / private mode */
  }
}

export async function getInboundQueueId(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', 'inbound_queue')) ?? null
}

export async function saveInboundQueueId(queueId: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', queueId.trim(), 'inbound_queue')
}

export async function getDailyCode(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', 'daily_code')) ?? null
}

export async function saveDailyCode(code: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', code.trim().toUpperCase(), 'daily_code')
}

export async function getDailyCodeUtcDay(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', 'daily_code_utc_day')) ?? null
}

export async function saveDailyCodeUtcDay(day: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', day.trim(), 'daily_code_utc_day')
}

export async function getQueueOwnerPubKey(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', 'queue_owner_pubkey')) ?? null
}

export async function saveQueueOwnerPubKey(pubKey: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', pubKey.trim(), 'queue_owner_pubkey')
}

export async function clearSession(): Promise<void> {
  const db = await getDb()
  await db.delete('meta', 'session_token')
  try {
    localStorage.removeItem(SESSION_MIRROR_KEY)
  } catch {
    /* ignore */
  }
  await db.delete('meta', 'inbound_queue')
  await db.delete('meta', 'daily_code')
  await db.delete('meta', 'daily_code_utc_day')
  await db.delete('meta', 'queue_owner_pubkey')
}
